import { forwardRef, useImperativeHandle, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { yupResolver } from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';

import { createResearchKpiValidationSchema } from 'features/ResearchKpiGroup/validation';
import { TextInput } from 'components/FormControl';
import Loading from 'components/Loading';
import { TOAST } from 'constant';
import { toast } from 'layout';
import { mapError } from 'utils/func';

const formOptions = { resolver: yupResolver(createResearchKpiValidationSchema) };

const ResearchKpiForm = forwardRef(({ isLoading, refetch }, ref) => {
  // #region Data
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [researchKpiControl, setResearchKpiControl] = useState(null);
  const [appliedYear, setAppliedYear] = useState(null);

  const {
    handleSubmit,
    control,
    trigger,
    reset,
    formState: { errors, isDirty, dirtyFields },
  } = useForm({ ...formOptions, mode: 'onChange' });
  // #endregion

  // #region Event
  useImperativeHandle(
    ref,
    () => ({
      open: (_researchKpiControl) => {
        setResearchKpiControl(_researchKpiControl);
        setVisible(true);
        if (_researchKpiControl.mode === 'create') {
          // value of research kpi group
          setAppliedYear(_researchKpiControl.appliedYear);
          reset({ researchKpiGroupId: _researchKpiControl.value.id });
        } else if (_researchKpiControl.mode === 'update') {
          const { id, name, englishName, researchKpiGroupId } = _researchKpiControl.value;
          reset({ id, name, englishName: englishName ?? '', researchKpiGroupId });
        }
      },
    }),
    []
  );

  const onSubmit = (data) => {
    if (researchKpiControl.mode === 'create') {
      const newData = { ...data, appliedYear };

      researchKpiControl.mutate(newData, {
        onSuccess: () => {
          toast(TOAST.SUCCESS, t('researchKpi.create.success'));
          reset();
          refetch();
          setVisible(false);
        },
        onError: (error) => {
          toast(TOAST.ERROR, mapError(t, error));
        },
      });
    } else {
      const { id, ...newData } = { ...data };

      researchKpiControl.mutate(
        { id, data: newData },
        {
          onSuccess: () => {
            toast(TOAST.SUCCESS, t('researchKpi.update.success'));
            refetch();
            setVisible(false);
          },
          onError: (error) => {
            toast(TOAST.ERROR, mapError(t, error));
          },
        }
      );
    }
  };

  const checkFormValid = async () => {
    const isValid = await trigger();
    if (!isValid) toast(TOAST.ERROR, t('errorMessage.validationErrorMessage'));
  };

  const checkKeyDown = (e) => {
    if (e.code === 'Enter') e.preventDefault();
  };
  // #endregion

  return (
    <Dialog
      header={
        // eslint-disable-next-line react/jsx-wrap-multilines
        <p className="text-blue-500">
          {researchKpiControl?.mode === 'create'
            ? t('researchKpi.create.title')
            : t('researchKpi.update.title')}
        </p>
      }
      visible={visible}
      style={{ maxWidth: '500px' }}
      onHide={() => {
        reset();
        setVisible(false);
      }}
    >
      <div className="relative">
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
        <form
          onSubmit={handleSubmit(onSubmit)}
          onKeyDown={checkKeyDown}
          className="p-fluid flex flex-column"
          autoComplete="off"
        >
          <div className="grid">
            <div className="col-12">
              <TextInput
                label={t('researchKpi.name')}
                name="name"
                control={control}
                errors={errors}
                isRequired
              />
            </div>
            <div className="col-12">
              <TextInput
                label={t('researchKpi.englishName')}
                name="englishName"
                control={control}
                errors={errors}
                isRequired
              />
            </div>
          </div>

          <div className="grid mt-2">
            <div className="col-6">
              <Button
                label={t('researchKpi.cancel')}
                type="button"
                severity="danger"
                onClick={() => {
                  reset();
                  setVisible(false);
                }}
              />
            </div>
            <div className="col-6">
              <Button
                label={
                  researchKpiControl?.mode === 'create'
                    ? t('researchKpi.add')
                    : t('researchKpi.save')
                }
                icon="pi pi-pencil"
                className="bg-cyan-400 border-cyan-400 hover:bg-cyan-500"
                type="submit"
                onClick={checkFormValid}
                disabled={!(isDirty && Object.keys(dirtyFields).length)}
              />
            </div>
          </div>
        </form>
        {isLoading && <Loading />}
      </div>
    </Dialog>
  );
});

export default ResearchKpiForm;

ResearchKpiForm.propTypes = {
  refetch: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};
ResearchKpiForm.defaultProps = {
  isLoading: false,
};
